/**
 * LLM API 配置区块（Base URL / API Key / 模型选择）
 */
import React from 'react';
import { ChevronDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { SystemModelConfig } from '../types';
import { useClickOutside } from '../hooks/useClickOutside';

interface LLMConfigSectionProps {
  modelConfig: SystemModelConfig;
  setModelConfig: (cfg: SystemModelConfig) => void;
  configSaving: boolean;
  configMsg: { type: 'success' | 'error'; text: string } | null;
  handleSaveConfig: () => void;
  availableModels: string[];
  fetchingModels: boolean;
  fetchModelsMsg: string | null;
  showModelDropdown: boolean;
  setShowModelDropdown: (v: boolean) => void;
  filteredModels: string[];
  handleFetchModels: () => void;
  includeAllModels: boolean;
  setIncludeAllModels: (v: boolean) => void;
  modelSearchRef: React.RefObject<HTMLDivElement | null>;
  modelInputRef: React.RefObject<HTMLInputElement | null>;
}

export default function LLMConfigSection({
  modelConfig, setModelConfig, configSaving, configMsg, handleSaveConfig,
  availableModels, fetchingModels, fetchModelsMsg,
  showModelDropdown, setShowModelDropdown, filteredModels,
  handleFetchModels, includeAllModels, setIncludeAllModels, modelSearchRef, modelInputRef,
}: LLMConfigSectionProps) {
  useClickOutside([modelSearchRef, modelInputRef], () => setShowModelDropdown(false), showModelDropdown);

  const renderModelDropdown = () => {
    if (!showModelDropdown) return null;
    if (filteredModels.length > 0) {
      return (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredModels.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setModelConfig({ ...modelConfig, model: m });
                setShowModelDropdown(false);
              }}
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${
                m === modelConfig.model ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      );
    }
    if (modelConfig.model && availableModels.length > 0) {
      return (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-400">
          无匹配模型，当前输入将作为自定义模型名称
        </div>
      );
    }
    return null;
  };

  const renderStatusMsg = () => {
    if (!configMsg) return null;
    return (
      <div className={`rounded-lg border px-4 py-2 text-sm ${
        configMsg.type === 'success'
          ? 'border-green-200 bg-green-50 text-green-700'
          : 'border-red-200 bg-red-50 text-red-700'
      }`}>
        {configMsg.text}
      </div>
    );
  };

  return (
    <section>
      <h3 className="text-sm font-semibold text-gray-900 mb-4">大模型配置</h3>
      <p className="text-xs text-gray-500 mb-4">
        配置大模型 API 连接参数，所有用户将共享此配置进行 AI 生成。
      </p>
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Base URL</label>
          <input
            type="text"
            value={modelConfig.base_url}
            onChange={(e) => setModelConfig({ ...modelConfig, base_url: e.target.value })}
            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
            placeholder="https://api.openai.com/v1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
          <input
            type="password"
            value={modelConfig.api_key}
            onChange={(e) => setModelConfig({ ...modelConfig, api_key: e.target.value })}
            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
            placeholder="sk-..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            系统默认（{modelConfig.model || '未配置'}）
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1" ref={modelSearchRef}>
              <input
                ref={modelInputRef}
                type="text"
                value={modelConfig.model}
                onChange={(e) => {
                  setModelConfig({ ...modelConfig, model: e.target.value });
                  if (!showModelDropdown && availableModels.length > 0) setShowModelDropdown(true);
                }}
                onFocus={() => { if (availableModels.length > 0) setShowModelDropdown(true); }}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border pr-8"
                placeholder="gpt-4"
              />
              {availableModels.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowModelDropdown(!showModelDropdown)}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  <ChevronDownIcon className="w-4 h-4" />
                </button>
              )}
              {renderModelDropdown()}
            </div>
            <button
              type="button"
              onClick={handleFetchModels}
              disabled={fetchingModels || !modelConfig.api_key.trim()}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              title="从 API 获取可用模型列表"
            >
              <ArrowPathIcon className={`w-4 h-4 mr-1 ${fetchingModels ? 'animate-spin' : ''}`} />
              {fetchingModels ? '获取中...' : '获取模型'}
            </button>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer mt-1">
            <input
              type="checkbox"
              checked={includeAllModels}
              onChange={(e) => setIncludeAllModels(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            显示全部模型（含私有化部署的非标准命名）
          </label>
          {fetchModelsMsg && (
            <p className={`mt-1 text-xs ${
              fetchModelsMsg.includes('失败') || fetchModelsMsg.includes('请先') ? 'text-red-500' : 'text-green-600'
            }`}>
              {fetchModelsMsg}
            </p>
          )}
        </div>
        {renderStatusMsg()}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">LLM 生成温度 <span className="text-xs text-gray-400">（0.0 - 2.0）</span></label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="2"
            value={modelConfig.llm_temperature ?? 0.3}
            onChange={(e) => setModelConfig({ ...modelConfig, llm_temperature: parseFloat(e.target.value) || 0.3 })}
            className="block w-32 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
          />
          <p className="mt-1 text-xs text-gray-400">全局兜底温度：当某个模型未单独设置温度时生效</p>
        </div>
        <button
          onClick={handleSaveConfig}
          disabled={configSaving}
          className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors"
        >
          {configSaving ? '保存中...' : '保存大模型配置'}
        </button>
      </div>
    </section>
  );
}
